
import React, { useState, useMemo } from 'react';
import { ExtractedData } from '../types';
import { generateCredentials } from '../services/asanaService';
import { Copy, ExternalLink, Check, User, Building2, Lock, Smartphone, Mail, MapPin, Globe, Terminal, AlertTriangle, Zap } from 'lucide-react';

interface ExternalFormHelperProps {
  data: ExtractedData;
}

export const ExternalFormHelper: React.FC<ExternalFormHelperProps> = ({ data }) => {
  const TARGET_URL = "https://myesalon.com/signup";
  const [activeTab, setActiveTab] = useState<'extension' | 'script' | 'manual'>('extension');
  const [scriptCopied, setScriptCopied] = useState(false);

  const credentials = useMemo(() => generateCredentials(data), [data]);

  const generateConsoleScript = () => {
    // Exclude large PDF data to prevent browser hangs and empty script boxes
    const { pdfData, ...cleanData } = data;
    const payload = { ...cleanData, generatedCreds: credentials };
    return `(function() {
    const data = ` + JSON.stringify(payload) + `;
    
    function setVal(id, val) {
        const el = document.getElementById(id);
        if (!el || !val) return;
        el.value = val;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        el.style.backgroundColor = '#dcfce7';
    }

    function setDropdown(id, val) {
        const el = document.getElementById(id);
        if (!el || !val) return;
        const options = Array.from(el.options);
        const match = options.find(o => 
            o.value.toLowerCase() === val.toLowerCase() || 
            o.text.toLowerCase().includes(val.toLowerCase())
        );
        if (match) {
            el.value = match.value;
            el.dispatchEvent(new Event('change', { bubbles: true }));
            el.style.backgroundColor = '#dcfce7';
        }
    }

    console.log('MyESALON: Starting population...');
    
    setVal('tb_firstname', data.ownerInfo.firstName);
    setVal('tb_lastname', data.ownerInfo.lastName);
    setVal('tb_contactphone', data.ownerInfo.phone);
    setVal('tb_email', data.ownerInfo.email);
    setVal('tb_businessname', data.businessInfo.name);
    setVal('tb_businessphone', data.businessInfo.phone);
    setVal('tb_address', data.businessInfo.address);
    setVal('tb_city', data.businessInfo.city);
    setVal('tb_zipcode', data.businessInfo.zip);
    setDropdown('dropdown_state', data.businessInfo.state);
    
    // Plan Selection mapping
    const planToSelect = data.package === 'Elite' || data.package === 'Supreme' 
        ? 'Elite Marketing Plan' 
        : data.package;
    if (planToSelect) {
        setDropdown('dropdown_plan', planToSelect);
    }

    setVal('tb_user', data.generatedCreds.username);
    setVal('tb_password', data.generatedCreds.password);
    
    if (data.businessInfo.website) {
        setVal('tb_domain', data.businessInfo.website);
    }
    
    alert('MyESALON: Form populated successfully!');
})();`;
  };

  const copyScript = () => {
    navigator.clipboard.writeText(generateConsoleScript());
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  return (
    <div className="glass-panel rounded-3xl shadow-xl border-white/50 overflow-hidden h-full flex flex-col">
      <div className="p-6 border-b border-white/30 bg-white/30 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-blue-600" />
          <h3 className="font-bold text-slate-800 text-xl">Signup Helper</h3>
        </div>
        <div className="flex gap-2 mt-4 p-1 bg-white/50 rounded-xl border border-white/40 shadow-inner">
          <button onClick={() => setActiveTab('extension')} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'extension' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-white/50'}`}>One-Click</button>
          <button onClick={() => setActiveTab('script')} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'script' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:bg-white/50'}`}>Script</button>
          <button onClick={() => setActiveTab('manual')} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'manual' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:bg-white/50'}`}>Manual</button>
        </div>
      </div>

      <div className="p-6 flex-1 overflow-y-auto">
        {activeTab === 'extension' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="bg-gradient-to-br from-indigo-900 to-blue-900 text-white p-6 rounded-2xl shadow-xl border border-indigo-700/50">
              <Zap className="w-8 h-8 text-yellow-400 mb-3" />
              <h4 className="font-bold text-lg mb-1">Auto-Fill Form</h4>
              <p className="text-indigo-200 text-sm mb-6 leading-relaxed">Launches the signup page and fills all fields automatically via the browser message bridge.</p>
              <button 
                onClick={() => {
                  const { pdfData, ...cleanData } = data;
                  window.postMessage({ type: 'MYESALON_EXTENSION_TRIGGER', payload: { ...cleanData, generatedCreds: credentials } }, '*');
                }} 
                className="w-full py-3.5 bg-white text-indigo-900 rounded-xl font-bold hover:bg-indigo-50 transition-colors shadow-lg"
              >
                Execute Auto-Fill
              </button>
            </div>
          </div>
        )}

        {activeTab === 'script' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="bg-amber-50/80 border border-amber-200/50 p-4 rounded-xl flex gap-3 backdrop-blur-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-800 font-medium">Copy this script, go to myesalon.com, open <b>F12 Console</b>, and paste it.</p>
            </div>
            <div className="relative group">
              <pre className="bg-slate-900/95 text-slate-300 p-4 rounded-xl text-[10px] overflow-x-auto max-h-64 font-mono custom-scrollbar border border-slate-700 shadow-inner">
                {generateConsoleScript()}
              </pre>
              <button onClick={copyScript} className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-md transition-colors border border-white/10">
                {scriptCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <a href={TARGET_URL} target="_blank" className="flex items-center justify-center gap-2 w-full py-3 bg-white/60 rounded-xl text-sm font-bold text-slate-700 border border-white/50 hover:bg-white/80 transition-all shadow-sm">
              <ExternalLink className="w-4 h-4" /> Open Signup Page
            </a>
          </div>
        )}

        {activeTab === 'manual' && (
          <div className="space-y-3 animate-in fade-in duration-300">
            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Field Guide</h5>
            <div className="grid grid-cols-1 gap-2">
              <CopyItem label="Business Name" value={data.businessInfo.name} />
              <CopyItem label="City" value={data.businessInfo.city} />
              <CopyItem label="State" value={data.businessInfo.state} />
              <CopyItem label="Email" value={data.businessInfo.email} />
              <CopyItem label="Username" value={credentials.username} />
              <CopyItem label="Password" value={credentials.password} />
            </div>
          </div>
        )}
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
      `}} />
    </div>
  );
};

const CopyItem = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="flex items-center justify-between p-3 bg-white/50 border border-white/50 rounded-xl hover:bg-white/80 hover:border-blue-300 text-left transition-all shadow-sm group">
      <div className="min-w-0 flex-1">
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-xs font-mono text-slate-700 truncate font-semibold">{value}</p>
      </div>
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />}
    </button>
  );
};
